import React, { useCallback, useEffect, useRef, useState } from "react";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
    Box,
    Button,
    IconButton,
    Paper,
    Tooltip,
    Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getLocalizedStoryFromTemplate } from "../../../admin/pzl_common/getEnStoryFromTemplate";
import PdfFormatDialog, {
    getPdfDownloadUseDisplayClue,
    PDF_FORMAT_DEFAULT_KEY,
} from "../../../admin/pzl_common/PdfFormatDialog";
import AuthService from "../../../services/AuthService";
import Logger from "../../../services/Logger";
import UtilService from "../../../services/UtilService";
import UserService from "../../../services/UserService";
import CustomSnackbar from "../../components/CustomSnackbar";
import BrowseStylePuzzleCard, { BROWSE_SHELF_CARD_WIDTH } from "../../browse-puzzles/BrowseStylePuzzleCard";
import CrossService from "../crossword/CrossService";
import GridService from "../grid/GridService";
import { getJigsawPlayBoardPath } from "../jigsaw/jigsawBrowseUtils";
import JigsawLibraryCard from "../jigsaw/JigsawLibraryCard";
import SlidingCard from "../../browse-sliding/SlidingCard";
import SlidingPlayMain from "../../../admin/story/SlidingPlayMain";
import StoryDialog from "../../../admin/pzl_common/StoryDialog";
import { isWordPuzzleOpenAccess } from "../common/puzzleTierAccess";
import { pickSlidingPlayImageUrl } from "../../browse-sliding/slidingBrowseUtils";
import { browseViewButtonSx } from "../../browse-jigsaw/JigsawBrowseStoryCard";
import { NOTICE } from "../../../dark-theme/palette";
import { usePagesTranslation } from "../../locales";

function libraryStoryRecord(puzzle) {
    if (!puzzle) return null;
    const tpl = puzzle.template;
    if (tpl && (tpl.story != null || tpl.storyJson != null)) return tpl;
    if (puzzle.story != null || puzzle.storyJson != null) return puzzle;
    return tpl || puzzle;
}

function libraryPuzzleHasStoryContent(puzzle) {
    const src = libraryStoryRecord(puzzle);
    if (!src) return false;
    const { title, text, imageUrl } = getLocalizedStoryFromTemplate(src);
    return Boolean(
        String(title || "").trim() ||
            String(text || "").trim() ||
            String(imageUrl || "").trim()
    );
}

function puzzleDisabled(puzzle) {
    return puzzle?.state === "DISABLED" || puzzle?.disabled === true;
}

const CompletePanel = ({
    puzzles = [],
    contentLanguage = "english",
    onUpgradeRequired,
    isInProgress = false,
}) => {
    const { t, lang } = usePagesTranslation();
    const [, setUserInfo] = useState(null);
    const [userPayPlan, setUserPayPlan] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const navigate = useNavigate();

    const [pdfFormatDialogOpen, setPdfFormatDialogOpen] = useState(false);
    const pdfPendingRef = useRef(null);

    const [storyDialogOpen, setStoryDialogOpen] = useState(false);
    const [storyViewPuzzle, setStoryViewPuzzle] = useState(null);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("error");

    useEffect(() => {
        const getUser = async () => {
            try {
                const userData = await UserService.getUserInfo();
                setUserInfo(userData);

                if (userData && userData.cloudUser) {
                    setUserPayPlan(UtilService.resolveWebPayPlan(userData.cloudUser));
                    const currSub = userData.cloudUser.currSub || {};
                    setSubscriptionStatus(currSub.status || null);
                } else {
                    setUserPayPlan(null);
                    setSubscriptionStatus(null);
                }
            } catch (error) {
                Logger.error("Error getting user:", error);
                setUserPayPlan(null);
                setSubscriptionStatus(null);
            }
        };

        getUser();
    }, []);

    const hasStandardPlan = UtilService.isStandardPayPlan(userPayPlan);

    const executePdfDownload = useCallback(
        async (puzzle, format) => {
            const templateId = puzzle.templateId ?? puzzle.template?.id;
            if (!templateId) {
                setSnackbarMessage(
                    t("library.pdf-missing-template", "Could not download PDF for this puzzle.")
                );
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                return;
            }
            const title = puzzle.title || "puzzle";
            const pdfUrl =
                format === "a4"
                    ? (puzzle.pdfA4Url ?? puzzle.template?.pdfA4Url)
                    : (puzzle.pdfLetterUrl ?? puzzle.template?.pdfLetterUrl);

            document.body.style.cursor = "wait";
            try {
                if (puzzle.type === "CROSSWORD") {
                    await CrossService.recordAndDownloadPdf(templateId, format, {
                        pdfUrl: pdfUrl || undefined,
                        title,
                        useDisplayClue: getPdfDownloadUseDisplayClue(),
                    });
                } else {
                    await GridService.recordAndDownloadPdf(templateId, format, {
                        pdfUrl: pdfUrl || undefined,
                        title,
                        useDisplayClue: getPdfDownloadUseDisplayClue(),
                    });
                }
            } catch (error) {
                Logger.error("Library PDF download:", error);
                setSnackbarMessage(t("puzzle.playGrid.pdf-error"));
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            } finally {
                document.body.style.cursor = "default";
            }
        },
        [t]
    );

    const handleLibraryPdfClick = useCallback(
        async (puzzle) => {
            if (puzzleDisabled(puzzle)) return;

            const type = String(puzzle?.type ?? "").toUpperCase();
            const isFree =
                type === "CROSSWORD" || type === "WORD_SEARCH" || type === "HIDDEN_MESSAGE"
                    ? isWordPuzzleOpenAccess(puzzle.difficultyLevel)
                    : puzzle.free === true;
            if (!isFree) {
                const authed = await AuthService.isAuthenticated();
                if (!authed) {
                    navigate("/login", {
                        state: {
                            from: { pathname: "/account/library" },
                            message: t("landing.login-standard-msg"),
                        },
                    });
                    return;
                }
                if (!hasStandardPlan) {
                    onUpgradeRequired("puzzle");
                    return;
                }
            }

            const userData = await UserService.getUserInfo().catch(() => null);
            const savedFormat =
                userData?.cloudUser?.details?.pdfFormat ||
                (typeof window !== "undefined"
                    ? window.localStorage.getItem(PDF_FORMAT_DEFAULT_KEY)
                    : null);

            pdfPendingRef.current = puzzle;
            if (savedFormat === "letter" || savedFormat === "a4") {
                await executePdfDownload(puzzle, savedFormat);
                pdfPendingRef.current = null;
            } else {
                setPdfFormatDialogOpen(true);
            }
        },
        [executePdfDownload, hasStandardPlan, navigate, onUpgradeRequired, t]
    );

    const handlePdfFormatConfirm = useCallback(
        async (format) => {
            const p = pdfPendingRef.current;
            pdfPendingRef.current = null;
            if (p) await executePdfDownload(p, format);
        },
        [executePdfDownload]
    );

    const handlePdfFormatDialogClose = useCallback(() => {
        setPdfFormatDialogOpen(false);
        pdfPendingRef.current = null;
    }, []);

    const openStoryDialog = useCallback((puzzle) => {
        setStoryViewPuzzle(puzzle);
        setStoryDialogOpen(true);
    }, []);

    const closeStoryDialog = useCallback(() => {
        setStoryDialogOpen(false);
        setStoryViewPuzzle(null);
    }, []);

    const storyLocalized = storyViewPuzzle
        ? (() => {
            const src = libraryStoryRecord(storyViewPuzzle);
            const { title, text, imageUrl } = getLocalizedStoryFromTemplate(src);
            const storyObj = src?.story || storyViewPuzzle?.template?.story;
            return { id: storyObj?.id, title, text, imageUrl, jigsaw: storyObj?.jigsaw, sliding: storyObj?.sliding };
        })()
        : { title: "", text: "", imageUrl: "" };

    const renderPrimaryActions = (puzzle) =>
        puzzle?.type === "SLIDING" ? (
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                <Tooltip
                    title={
                        libraryPuzzleHasStoryContent(puzzle)
                            ? t("library.open-story", "Story")
                            : t("library.no-story-available", "No story available")
                    }
                >
                    <span>
                        <IconButton
                            size="small"
                            disabled={!libraryPuzzleHasStoryContent(puzzle)}
                            onClick={() => openStoryDialog(puzzle)}
                            sx={{ color: NOTICE.info.text }}
                            aria-label={t("library.open-story", "Story")}
                        >
                            <AutoStoriesIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        ) : (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <Tooltip title={t("puzzle.playGrid.download-pdf")}>
                <span>
                    <IconButton
                        size="small"
                        disabled={puzzleDisabled(puzzle)}
                        onClick={() => handleLibraryPdfClick(puzzle)}
                        sx={{ color: NOTICE.info.text }}
                        aria-label={t("puzzle.playGrid.download-pdf")}
                    >
                        <PictureAsPdfIcon />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip
                title={
                    libraryPuzzleHasStoryContent(puzzle)
                        ? t("library.open-story", "Story")
                        : t("library.no-story-available", "No story available")
                }
            >
                <span>
                    <IconButton
                        size="small"
                        disabled={!libraryPuzzleHasStoryContent(puzzle)}
                        onClick={() => openStoryDialog(puzzle)}
                        sx={{ color: NOTICE.info.text }}
                        aria-label={t("library.open-story", "Story")}
                    >
                        <AutoStoriesIcon />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
        );

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "stretch",
                }}
            >
                {puzzles.map((puzzle, index) => {
                    const category =
                        puzzle.type === "CROSSWORD"
                            ? "CROSSWORD"
                            : puzzle.type === "HIDDEN_MESSAGE"
                              ? "HIDDEN_MESSAGE"
                              : puzzle.type === "SLIDING"
                                ? "SLIDING"
                                : puzzle.type === "JIGSAW"
                                  ? "JIGSAW"
                                  : puzzle.type === "WORD_WHEEL"
                                    ? "WORD_WHEEL"
                                  : "WORD_SEARCH";

                    const isJigsaw = category === "JIGSAW";
                    const isSliding = category === "SLIDING";
                    const isWordWheel = category === "WORD_WHEEL";

                    return (
                        <Box
                            key={puzzle.id || index}
                            sx={{
                                width: BROWSE_SHELF_CARD_WIDTH,
                                flexShrink: 0,
                                display: "flex",
                                flexDirection: "column",
                                alignSelf: "stretch",
                            }}
                        >
                            {isJigsaw ? (
                                <Box sx={{ flex: "1 1 auto", minHeight: 0, width: "100%" }}>
                                    <JigsawLibraryCard
                                        puzzle={puzzle}
                                        onPlayClick={(p) => navigate(getJigsawPlayBoardPath(p.id, p.jigsawId))}
                                        buttonText="Play"
                                        lang={lang}
                                        t={t}
                                    />
                                </Box>
                            ) : isSliding ? (
                                <SlidingCard
                                    puzzle={puzzle}
                                    userPayPlan={userPayPlan || (hasStandardPlan ? "STANDARD" : "FREE")}
                                    subscriptionStatus={subscriptionStatus}
                                    buttonText="Play"
                                    forceUnlock={true}
                                    hideTierBadges
                                    hideWordCountChip
                                    browseStoriesChrome
                                    hideClusteredBubbles
                                    actionButtonSx={browseViewButtonSx}
                                    primaryActionReplacement={puzzle.disabled ? (
                                        <Button size="small" variant="contained" disabled sx={browseViewButtonSx}>
                                            Play
                                        </Button>
                                    ) : undefined}
                                    belowDifficultySlot={
                                        <Box sx={{ width: "100%" }}>
                                            {puzzle.title && (
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 700,
                                                        lineHeight: 1.35,
                                                        mb: 0.75,
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: "vertical",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    {puzzle.title}
                                                </Typography>
                                            )}
                                            {puzzle.gridSize && puzzle.story ? (
                                                <Box sx={{ width: "100%", aspectRatio: "1 / 1", overflow: "hidden", borderRadius: 1, pointerEvents: "none", bgcolor: "grey.900" }}>
                                                    <SlidingPlayMain
                                                        imageUrl={pickSlidingPlayImageUrl(puzzle.story)}
                                                        gridSize={puzzle.gridSize}
                                                        startingLayout={puzzle.slidingPlay?.currentLayout ?? puzzle.startingLayout}
                                                        hideCompletedCaption
                                                        fillParent
                                                        readOnly
                                                    />
                                                </Box>
                                            ) : null}
                                        </Box>
                                    }
                                    storyTeaserTrailingActions={renderPrimaryActions(puzzle)}
                                />
                            ) : isWordWheel ? (
                                <Paper sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
                                    <Typography sx={{ fontWeight: 700 }}>{puzzle.title}</Typography>
                                    <Button
                                        variant="contained"
                                        onClick={() => navigate(`/puzzle/play-word-wheel/${encodeURIComponent(puzzle.id)}?mode=daily`)}
                                        sx={{ ...browseViewButtonSx, mt: "auto" }}
                                    >
                                        Replay
                                    </Button>
                                </Paper>
                            ) : (
                                <BrowseStylePuzzleCard
                                    category={category}
                                    puzzle={puzzle}
                                    storyTeaserRow={puzzle}
                                    contentLanguage={contentLanguage}
                                    lang={lang}
                                    t={t}
                                    userPayPlan={userPayPlan || (hasStandardPlan ? "STANDARD" : "FREE")}
                                    subscriptionStatus={subscriptionStatus}
                                    buttonText="Play"
                                    isInProgress={isInProgress}
                                    forceUnlock={true}
                                    hideClusteredBubbles
                                    actionButtonSx={browseViewButtonSx}
                                    storyTeaserTrailingActions={renderPrimaryActions(puzzle)}
                                />
                            )}
                        </Box>
                    );
                })}
            </Box>

            {puzzles.length === 0 && (
                <Paper sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {isInProgress ? t("library.no-puzzles-in-progress") : t("library.no-completed-puzzles")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isInProgress ? t("library.start-puzzle-hint") : t("library.complete-puzzles-hint")}
                    </Typography>
                </Paper>
            )}

            <PdfFormatDialog
                open={pdfFormatDialogOpen}
                onClose={handlePdfFormatDialogClose}
                onConfirm={handlePdfFormatConfirm}
            />

            <StoryDialog
                open={storyDialogOpen}
                onClose={closeStoryDialog}
                story={storyLocalized}
                fallbackTitle={storyViewPuzzle?.title}
            />

            <CustomSnackbar
                open={snackbarOpen}
                setOpen={setSnackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
            />
        </Box>
    );
};

export default CompletePanel;
